import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from 'components/ui/sheet';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Download, Loader2 } from 'lucide-react';

const DetailRow = ({ label, value, valueClass }) => (
  <div className="flex justify-between items-start py-3 border-b border-gray-100 last:border-0 gap-4">
    <span className="text-sm text-gray-500 flex-shrink-0">{label}</span>
    <span className={`text-sm font-medium text-gray-900 text-right break-words ${valueClass || ''}`}>{value}</span>
  </div>
);

const ExportDetailDrawer = ({ visible, onClose, record, onDownload, isDownloading }) => {
  const parsedFilters = React.useMemo(() => {
    if (!record?.filters) return {};
    if (typeof record.filters === 'string') {
      try {
        return JSON.parse(record.filters);
      } catch (e) {
        return {};
      }
    }
    return record.filters;
  }, [record?.filters]);

  if (!record) return null;

  const yearRange = parsedFilters.year_range || (Array.isArray(parsedFilters.yearRange) ? `${parsedFilters.yearRange[0]} - ${parsedFilters.yearRange[1]}` : parsedFilters.yearRange) || '—';
  const subjectArea = (parsedFilters.subject_area || parsedFilters.subjectArea || []).join(', ') || '—';

  return (
    <Sheet open={visible} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-[400px] sm:w-[540px] bg-white border-l border-gray-200 p-0 flex flex-col">
        <SheetHeader className="p-6 border-b border-gray-200">
          <SheetTitle>Export Details</SheetTitle>
          <SheetDescription>
            Detailed information about this export task.
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex flex-col">
            <DetailRow label="Export Name" value={record.title} />
            <DetailRow label="Source" value={record.source} valueClass="capitalize" />
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-sm text-gray-500">Status</span>
              <Badge variant={record.status === 'completed' || record.status === 'ready' ? 'default' : record.status === 'failed' ? 'destructive' : 'secondary'} className="capitalize">
                {record.status}
              </Badge>
            </div>
            {record.error_message && (
              <DetailRow label="Error" value={record.error_message} valueClass="text-red-600 font-normal text-xs" />
            )}
            <DetailRow label="Format" value={(record.export_format || 'PDF').toUpperCase()} />
            <DetailRow label="Entity / Scope" value={record.entity || '—'} />
            <DetailRow label="Entity Type" value={(record.entity_type || '—').toUpperCase()} />
            <DetailRow label="Year Range" value={yearRange} />
            <DetailRow label="Subject Area" value={subjectArea} />
            {Array.isArray(record.metrics) && record.metrics.length > 0 ? (
              <DetailRow 
                label="Selected Metrics" 
                value={`${record.metrics.length} metrics (${record.metrics.join(', ')})`} 
                valueClass="text-xs text-gray-700"
              />
            ) : (
              <DetailRow 
                label="Sections Included" 
                value={Array.isArray(record.sections) ? `${record.sections.length} sections (${record.sections.join(', ')})` : '—'} 
                valueClass="text-xs text-gray-700"
              />
            )}
            <DetailRow 
              label="Created At" 
              value={record.createdAt ? new Date(record.createdAt).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'} 
            />
          </div>
        </div>
        
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {(record.status === 'ready' || record.status === 'completed') && onDownload && (
            <Button
              className="bg-explore text-white hover:bg-explore/90"
              disabled={isDownloading}
              onClick={() => onDownload(record)}
            >
              {isDownloading ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="mr-1.5 h-4 w-4" />
                  Download
                </>
              )}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ExportDetailDrawer;
